import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { io, Socket } from 'socket.io-client';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalTextArea } from '../../../components/GlobalComponents/GlobalTextArea';
import { GlobalDropdownField, DropdownOption } from '../../../components/GlobalComponents/GlobalDropdownField';
import MediaPickerDialog from '../../MediaLibrary/MediaPickerDialog';
import type { MediaItem } from '../../MediaLibrary/mediaTypes';

interface StudyMaterialFile {
  file_name: string;
  key: string;
  url: string;
  size: number;
  mime_type: string;
  uploaded_at: string;
}

interface StudyMaterial {
  id: string;
  chapter_id: string;
  module_id: string;
  course_id: string;
  institute_id: string;
  title: string;
  body: string;
  order: number;
  status: string;
  files: StudyMaterialFile[];
  video_links: string[];
  created_at: string;
  created_by: string;
}

interface AddStudyMaterialModalProps {
  visible: boolean;
  chapterId: string;
  editingMaterial?: StudyMaterial | null;
  onHide: () => void;
  onSuccess?: () => void;
}

interface UploadedFile {
  file: File;
  name: string;
  size: number;
}

interface UploadedVideo {
  file: File;
  name: string;
  size: number;
}

interface VideoStatusEvent {
  video_id:      string;
  status:        'ready' | 'failed';
  m3u8_url:      string | null;
  error_message: string | null;
  updated_at:    string;
}

let globalSocket: Socket | null = null;

const getOrCreateSocket = async (): Promise<Socket | null> => {
  if (globalSocket?.connected) return globalSocket;
  try {
    const res = await apiClient.get('/v1/socket-token');
    const { socket_token } = res.data;
    if (!socket_token) throw new Error('No socket_token in response');
    globalSocket = io(window.location.origin, {
      path:                '/node-ws/socket.io',
      auth:                { socket_token },
      reconnection:        true,
      reconnectionAttempts: 5,
      reconnectionDelay:   2000,
    });
    return globalSocket;
  } catch (err) {
    console.error('Socket init failed:', err);
    return null;
  }
};

const AddStudyMaterialModal: React.FC<AddStudyMaterialModalProps> = ({
  visible,
  chapterId,
  editingMaterial,
  onHide,
  onSuccess,
}) => {
  const toast         = useRef<Toast>(null);
  const fileInputRef  = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const pendingVideoIds = useRef<Set<string>>(new Set());
  const socketRef       = useRef<Socket | null>(null);

  const [loading,        setLoading]        = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error,          setError]          = useState<string | null>(null);
  const [uploadedFiles,  setUploadedFiles]  = useState<UploadedFile[]>([]);
  const [uploadedVideos, setUploadedVideos] = useState<UploadedVideo[]>([]);
  const [libraryFiles,   setLibraryFiles]   = useState<MediaItem[]>([]);
  const [pickerOpen,     setPickerOpen]     = useState(false);

  const [videoProcessingStatus, setVideoProcessingStatus] = useState<Record<string, 'processing' | 'ready' | 'failed'>>({});

  const [formData, setFormData] = useState({
    title:       '',
    body:        '',
    status:      'active',
    order:       1,
    video_links: [''],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const MAX_FILES          = 3;
  const MAX_TOTAL_SIZE     = 20 * 1024 * 1024;
  const ALLOWED_FORMATS    = [
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg', 'image/jpg', 'image/png',
  ];
  const ALLOWED_EXTENSIONS  = ['pdf', 'xls', 'xlsx', 'doc', 'docx', 'jpeg', 'jpg', 'png'];
  const MAX_VIDEOS          = 3;
  const MAX_VIDEO_SIZE      = 500 * 1024 * 1024;
  const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska'];
  const ALLOWED_VIDEO_EXTS  = ['mp4', 'mov', 'avi', 'mkv'];

  const statusOptions: DropdownOption[] = [
    { label: 'Active',   value: 'active'   },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Draft',    value: 'draft'    },
  ];

  const showSuccess = (summary: string, detail: string) =>
    toast.current?.show({ severity: 'success', summary, detail, life: 3200 });

  const showError = (detail: string, summary = 'Error') =>
    toast.current?.show({ severity: 'error', summary, detail, life: 4500 });

  const handleVideoStatus = useCallback((data: VideoStatusEvent) => {
    if (!pendingVideoIds.current.has(data.video_id)) return;
    setVideoProcessingStatus((prev) => ({ ...prev, [data.video_id]: data.status }));
    if (data.status === 'ready') {
      toast.current?.show({ severity: 'success', summary: '✅ Video Ready', detail: 'A video has finished processing and is now available.', life: 5000 });
      pendingVideoIds.current.delete(data.video_id);
      onSuccess?.();
    }
    if (data.status === 'failed') {
      toast.current?.show({ severity: 'error', summary: '❌ Video Processing Failed', detail: data.error_message || 'A video failed to process. Please try uploading again.', life: 6000 });
      pendingVideoIds.current.delete(data.video_id);
    }
    if (pendingVideoIds.current.size === 0) {
      socketRef.current?.off('video_status', handleVideoStatus);
    }
  }, [onSuccess]);

  const initSocketListener = useCallback(async () => {
    const socket = await getOrCreateSocket();
    if (!socket) { console.warn('Could not connect to WebSocket'); return; }
    socketRef.current = socket;
    socket.off('video_status', handleVideoStatus);
    socket.on('video_status', handleVideoStatus);
    socket.on('connect',       () => console.log('🔌 Socket connected:', socket.id));
    socket.on('connect_error', (err) => console.error('Socket connect_error:', err.message));
  }, [handleVideoStatus]);

  useEffect(() => {
    return () => { socketRef.current?.off('video_status', handleVideoStatus); };
  }, [handleVideoStatus]);

  useEffect(() => {
    if (editingMaterial) {
      setFormData({
        title:       editingMaterial.title,
        body:        editingMaterial.body,
        status:      editingMaterial.status || 'active',
        order:       editingMaterial.order  ?? 1,
        video_links: editingMaterial.video_links.length > 0 ? editingMaterial.video_links : [''],
      });
    } else {
      setFormData({ title: '', body: '', status: 'active', order: 1, video_links: [''] });
    }
    setUploadedFiles([]);
    setLibraryFiles([]);
    setUploadedVideos([]);
    setErrors({});
    setError(null);
    setUploadProgress(0);
    setVideoProcessingStatus({});
    pendingVideoIds.current.clear();
  }, [editingMaterial, visible]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleVideoLinkChange = (index: number, value: string) => {
    const newLinks = [...formData.video_links];
    newLinks[index] = value;
    setFormData((prev) => ({ ...prev, video_links: newLinks }));
  };

  const addVideoLink = () => {
    if (formData.video_links.length < 5)
      setFormData((prev) => ({ ...prev, video_links: [...prev.video_links, ''] }));
  };

  const removeVideoLink = (index: number) => {
    const newLinks = formData.video_links.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, video_links: newLinks.length > 0 ? newLinks : [''] }));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k     = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i     = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileExtension = (filename: string): string =>
    filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();

  const getTotalFilesSize = (): number =>
    uploadedFiles.reduce((total, f) => total + f.size, 0);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    if (uploadedFiles.length + libraryFiles.length + files.length > MAX_FILES) {
      showError(`Maximum ${MAX_FILES} files allowed`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const newFiles: UploadedFile[] = [];
    const fileErrors: string[]     = [];
    Array.from(files).forEach((file) => {
      const ext = getFileExtension(file.name);
      if (!ALLOWED_FORMATS.includes(file.type) && !ALLOWED_EXTENSIONS.includes(ext)) {
        fileErrors.push(`${file.name}: Invalid format`);
        return;
      }
      newFiles.push({ file, name: file.name, size: file.size });
    });
    const wouldBeTotal = getTotalFilesSize() + newFiles.reduce((s, f) => s + f.size, 0);
    if (wouldBeTotal > MAX_TOTAL_SIZE) {
      showError(`Total size cannot exceed ${formatFileSize(MAX_TOTAL_SIZE)}`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (fileErrors.length > 0) showError(fileErrors.join(', '));
    setUploadedFiles((prev) => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = (index: number) =>
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));

  const handleVideoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    if (uploadedVideos.length + files.length > MAX_VIDEOS) {
      showError(`Maximum ${MAX_VIDEOS} videos allowed`);
      if (videoInputRef.current) videoInputRef.current.value = '';
      return;
    }
    const newVideos: UploadedVideo[] = [];
    const videoErrors: string[]      = [];
    Array.from(files).forEach((file) => {
      const ext = getFileExtension(file.name);
      if (!ALLOWED_VIDEO_TYPES.includes(file.type) && !ALLOWED_VIDEO_EXTS.includes(ext)) {
        videoErrors.push(`${file.name}: Invalid format. Allowed: MP4, MOV, AVI, MKV`);
        return;
      }
      if (file.size > MAX_VIDEO_SIZE) {
        videoErrors.push(`${file.name}: Exceeds 500MB limit`);
        return;
      }
      newVideos.push({ file, name: file.name, size: file.size });
    });
    if (videoErrors.length > 0) {
      showError(videoErrors.join('\n'));
      if (videoInputRef.current) videoInputRef.current.value = '';
      return;
    }
    setUploadedVideos((prev) => [...prev, ...newVideos]);
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const handleRemoveVideo = (index: number) =>
    setUploadedVideos((prev) => prev.filter((_, i) => i !== index));

  const getFileIcon = (filename: string): string => {
    const ext = getFileExtension(filename);
    switch (ext) {
      case 'pdf':               return 'pi pi-file-pdf';
      case 'doc': case 'docx':  return 'pi pi-file-word';
      case 'xls': case 'xlsx':  return 'pi pi-file-excel';
      case 'jpg': case 'jpeg':
      case 'png':               return 'pi pi-image';
      default:                  return 'pi pi-file';
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title  = 'Title is required';
    if (!formData.body.trim())  newErrors.body   = 'Content is required';
    if (!formData.status)       newErrors.status = 'Status is required';
    if (!formData.order || formData.order < 1) newErrors.order = 'Order must be a positive number';

    const validLinks = formData.video_links.filter((l) => l.trim() !== '');
    if (validLinks.length > 5) newErrors.video_links = 'Maximum 5 video links allowed';
    for (const link of validLinks) {
      try { new URL(link); } catch {
        newErrors.video_links = 'All video links must be valid URLs';
        break;
      }
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      showError('Please correct all highlighted fields', 'Validation Failed');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    setUploadProgress(0);

    const payload = new FormData();
    payload.append('chapter_id', chapterId);
    payload.append('title',      formData.title.trim());
    payload.append('body',       formData.body.trim());
    payload.append('status',     formData.status);
    payload.append('order',      String(formData.order));

    formData.video_links
      .filter((l) => l.trim() !== '')
      .forEach((link) => payload.append('video_links[]', link.trim()));

    uploadedFiles.forEach((f)  => payload.append('files[]',  f.file));
    uploadedVideos.forEach((v) => payload.append('videos[]', v.file));
    libraryFiles.forEach((m) => payload.append('media_ids[]', m.id));

    try {
      const isEditing = !!editingMaterial;
      const response  = isEditing
        ? await apiClient.put(`/v1/study-materials/${editingMaterial!.id}`, payload, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / (e.total ?? 1))),
          })
        : await apiClient.post('/v1/study-materials', payload, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / (e.total ?? 1))),
          });

      if (response.data.success) {
        const videosData: any[] = response.data.data?.videos         ?? [];
        const videosProcessing  = response.data.data?.videos_processing ?? 0;

        if (videosProcessing > 0 && videosData.length > 0) {
          videosData.forEach((v: any) => {
            if (v.video_id) {
              pendingVideoIds.current.add(v.video_id);
              setVideoProcessingStatus((prev) => ({ ...prev, [v.video_id]: 'processing' }));
            }
          });
          await initSocketListener();
          showSuccess(
            'Study Material Saved',
            `Saved successfully. ${videosProcessing} video(s) are processing — you'll be notified here when ready.`
          );
        } else {
          showSuccess(
            'Study Material Saved',
            isEditing ? 'Study material updated successfully.' : 'Study material created successfully.'
          );
        }

        onSuccess?.();
        setTimeout(() => onHide(), 900);
      } else {
        showError(response.data.message || 'Operation failed');
      }
    } catch (err: any) {
      const msg =
        err.response?.data?.error   ||
        err.response?.data?.message ||
        `HTTP ${err.response?.status}: Failed to save study material.`;
      showError(msg);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    setFormData({ title: '', body: '', status: 'active', order: 1, video_links: [''] });
    setUploadedFiles([]);
    setLibraryFiles([]);
    setUploadedVideos([]);
    setErrors({});
    setError(null);
    setUploadProgress(0);
    setVideoProcessingStatus({});
    if (fileInputRef.current)  fileInputRef.current.value  = '';
    if (videoInputRef.current) videoInputRef.current.value = '';
    onHide();
  };

  const totalSize       = getTotalFilesSize();
  const canUploadMore   = (uploadedFiles.length + libraryFiles.length) < MAX_FILES  && totalSize < MAX_TOTAL_SIZE;
  const existingFileCount = editingMaterial?.files?.length ?? 0;   // already attached to the material being edited
  const canUploadVideo  = uploadedVideos.length < MAX_VIDEOS;
  const processingCount = Object.values(videoProcessingStatus).filter((s) => s === 'processing').length;

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{ width: '90vw', maxWidth: '900px' }}
      dismissableMask
      draggable={false}
      pt={{
        root: {
          style: {
            borderRadius: '20px',
            overflow:     'hidden',
            boxShadow:    '0 25px 50px -12px rgba(0,0,0,0.25)',
          },
        },
      }}
    >
      <Toast ref={toast} position="top-right" />

      <div>
        {/* ── Header ── */}
        <div style={{ background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)', borderRadius: '16px', padding: '24px', marginBottom: '24px', color: 'white', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '150px', height: '150px', background: 'rgba(212, 241, 126, 0.1)', borderRadius: '50%', filter: 'blur(40px)' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-block', padding: '4px 10px', background: 'rgba(212, 241, 126, 0.2)', borderRadius: '16px', marginBottom: '8px', fontSize: '0.65rem', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              Study Material Management
            </div>
            <h2 style={{ margin: '0 0 6px 0', fontSize: '1.4rem', fontWeight: '700' }}>
              {editingMaterial ? 'Edit Study Material' : 'Create Study Material'}
            </h2>
            <p style={{ margin: '0', color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
              {editingMaterial ? 'Update study material details' : 'Share educational resources with students'}
            </p>
            {processingCount > 0 && (
              <div style={{ marginTop: '12px', padding: '8px 14px', background: 'rgba(212, 241, 126, 0.15)', border: '1px solid rgba(212, 241, 126, 0.3)', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem' }}>
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.9rem' }} />
                {processingCount} video{processingCount > 1 ? 's' : ''} processing in background... You'll be notified when ready.
              </div>
            )}
          </div>
        </div>

        {error && <Message severity="error" text={error} style={{ marginBottom: '20px', fontSize: '0.9rem' }} />}

        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px', border: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* ── Title + Order row ── */}
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <GlobalInputField
                  label="Material Title"
                  required
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="e.g., Introduction to Variables"
                  error={errors.title}
                />
              </div>
              <div style={{ width: '120px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Order <span style={{ color: '#e53e3e' }}>*</span>
                </label>
                <input
                  type="number"
                  min={1}
                  value={formData.order}
                  onChange={(e) => handleInputChange('order', parseInt(e.target.value) || 1)}
                  style={{
                    width: '100%', padding: '10px 12px',
                    border: errors.order ? '1px solid #e53e3e' : '1px solid #d1d5db',
                    borderRadius: '8px', fontSize: '0.9rem',
                    outline: 'none', boxSizing: 'border-box',
                    color: '#1a1a1a', background: 'white',
                  }}
                />
                {errors.order && (
                  <small style={{ color: '#e53e3e', marginTop: '4px', display: 'block', fontSize: '0.75rem' }}>
                    {errors.order}
                  </small>
                )}
              </div>
            </div>

            {/* ── Body ── */}
            <GlobalTextArea
              label="Material Content"
              required
              value={formData.body}
              onChange={(e) => handleInputChange('body', e.target.value)}
              placeholder="Write detailed content, notes, or instructions here..."
              error={errors.body}
              rows={8}
            />

            {/* ── Status ── */}
            <GlobalDropdownField
              label="Status"
              required
              value={formData.status}
              onChange={(val) => handleInputChange('status', val)}
              options={statusOptions}
              placeholder="Select status"
              error={errors.status}
            />

            {/* ── External Video Links ── */}
            <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '12px', border: '1px solid #e9ecef' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#0f3d3e' }}>
                  <i className="pi pi-link" style={{ marginRight: '8px' }} />
                  External Video Links (Optional)
                </h3>
                <span style={{ fontSize: '0.8rem', color: '#666' }}>
                  {formData.video_links.filter((l) => l.trim()).length}/5
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {formData.video_links.map((link, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <GlobalInputField
                      value={link}
                      onChange={(e) => handleVideoLinkChange(index, e.target.value)}
                      placeholder="https://youtube.com/watch?v=..."
                      error=""
                    />
                    {formData.video_links.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeVideoLink(index)}
                        style={{ padding: '10px 12px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '8px', cursor: 'pointer', flexShrink: 0 }}
                      >
                        <i className="pi pi-trash" style={{ fontSize: '0.85rem' }} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {errors.video_links && (
                <small style={{ color: '#e53e3e', marginTop: '8px', display: 'block' }}>
                  {errors.video_links}
                </small>
              )}
              {formData.video_links.length < 5 && (
                <button
                  type="button"
                  onClick={addVideoLink}
                  style={{ marginTop: '12px', padding: '8px 14px', background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', color: '#0f3d3e', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500', width: '100%' }}
                >
                  <i className="pi pi-plus" style={{ marginRight: '6px' }} />
                  Add Another Video Link
                </button>
              )}
            </div>

            {/* ── Video File Uploads ── */}
            <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '12px', border: '1px solid #e9ecef' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#0f3d3e' }}>
                  <i className="pi pi-video" style={{ marginRight: '8px' }} />
                  Upload Videos (Optional)
                </h3>
                <span style={{ fontSize: '0.8rem', color: '#666' }}>
                  {uploadedVideos.length}/{MAX_VIDEOS} • Max 500MB each
                </span>
              </div>
              <input ref={videoInputRef} type="file" multiple accept=".mp4,.mov,.avi,.mkv" onChange={handleVideoSelect} style={{ display: 'none' }} />
              <button
                type="button"
                onClick={() => videoInputRef.current?.click()}
                disabled={!canUploadVideo}
                style={{ width: '100%', padding: '14px', border: '2px dashed #cbd5e0', borderRadius: '8px', background: canUploadVideo ? 'white' : '#f5f5f5', color: canUploadVideo ? '#0f3d3e' : '#999', cursor: canUploadVideo ? 'pointer' : 'not-allowed', fontSize: '0.9rem', fontWeight: '500' }}
              >
                <i className="pi pi-upload" style={{ marginRight: '8px' }} />
                {canUploadVideo ? 'Choose video files' : 'Limit reached (3 videos max)'}
              </button>
              <div style={{ marginTop: '8px', fontSize: '0.75rem', color: '#666', textAlign: 'center' }}>
                Allowed: MP4, MOV, AVI, MKV • Videos are processed in background after upload
              </div>

              {uploadedVideos.length > 0 && (
                <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {uploadedVideos.map((videoObj, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                        <i className="pi pi-play-circle" style={{ fontSize: '1.4rem', color: '#0f3d3e' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {videoObj.name}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#666', display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <span>{formatFileSize(videoObj.size)}</span>
                            <span style={{ padding: '1px 8px', background: '#fef3c7', color: '#92400e', borderRadius: '10px', fontSize: '0.7rem' }}>
                              Will be processed after upload
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveVideo(index)}
                        style={{ padding: '6px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Post-upload live processing status */}
              {Object.entries(videoProcessingStatus).length > 0 && (
                <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {Object.entries(videoProcessingStatus).map(([videoId, status]) => (
                    <div
                      key={videoId}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '8px 12px', borderRadius: '8px',
                        background: status === 'ready' ? '#f0fdf4' : status === 'failed' ? '#fef2f2' : '#fffbeb',
                        border: `1px solid ${status === 'ready' ? '#bbf7d0' : status === 'failed' ? '#fecaca' : '#fde68a'}`,
                        fontSize: '0.8rem',
                      }}
                    >
                      {status === 'processing' && <i className="pi pi-spin pi-spinner" style={{ color: '#d97706' }} />}
                      {status === 'ready'      && <i className="pi pi-check-circle"    style={{ color: '#16a34a' }} />}
                      {status === 'failed'     && <i className="pi pi-times-circle"    style={{ color: '#dc2626' }} />}
                      <span style={{ color: status === 'ready' ? '#15803d' : status === 'failed' ? '#dc2626' : '#92400e' }}>
                        {videoId} —{' '}
                        {status === 'processing' && 'Processing...'}
                        {status === 'ready'      && 'Ready ✓'}
                        {status === 'failed'     && 'Processing failed'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── File Attachments ── */}
            <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '12px', border: '1px solid #e9ecef' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#0f3d3e' }}>
                  <i className="pi pi-paperclip" style={{ marginRight: '8px' }} />
                  File Attachments (Optional)
                </h3>
                <span style={{ fontSize: '0.8rem', color: '#666' }}>
                  {uploadedFiles.length + libraryFiles.length}/{MAX_FILES} • {formatFileSize(totalSize)} / {formatFileSize(MAX_TOTAL_SIZE)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                disabled={uploadedFiles.length + existingFileCount >= MAX_FILES && libraryFiles.length === 0}
                style={{ width: '100%', padding: '14px', marginBottom: '10px', border: '2px solid #0f3d3e', borderRadius: '8px', background: '#f0f7f7', color: '#0f3d3e', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600' }}
              >
                <i className="pi pi-images" style={{ marginRight: '8px' }} />
                Choose from Media Library
              </button>
              <input ref={fileInputRef} type="file" multiple accept=".pdf,.xls,.xlsx,.doc,.docx,.jpg,.jpeg,.png" onChange={handleFileSelect} style={{ display: 'none' }} />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!canUploadMore}
                style={{ width: '100%', padding: '14px', border: '2px dashed #cbd5e0', borderRadius: '8px', background: canUploadMore ? 'white' : '#f5f5f5', color: canUploadMore ? '#0f3d3e' : '#999', cursor: canUploadMore ? 'pointer' : 'not-allowed', fontSize: '0.9rem', fontWeight: '500' }}
              >
                <i className="pi pi-upload" style={{ marginRight: '8px' }} />
                {canUploadMore ? 'Choose files (max 10MB total)' : 'Limit reached'}
              </button>
              <div style={{ marginTop: '8px', fontSize: '0.75rem', color: '#666', textAlign: 'center' }}>
                Allowed: PDF, Word, Excel, JPEG, PNG
              </div>

              {libraryFiles.length > 0 && (
                <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {libraryFiles.map((f) => (
                    <div key={f.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#f0f7f7', border: '1px solid #b8d4d4', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                        <i className="pi pi-images" style={{ fontSize: '1.4rem', color: '#0f3d3e' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.title}</div>
                          <div style={{ fontSize: '0.75rem', color: '#0f3d3e' }}>{formatFileSize(f.size)} • From Media Library</div>
                        </div>
                      </div>
                      <button type="button" onClick={() => setLibraryFiles((prev) => prev.filter((x) => x.id !== f.id))} style={{ padding: '6px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Remove</button>
                    </div>
                  ))}
                </div>
              )}
              {uploadedFiles.length > 0 && (
                <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {uploadedFiles.map((fileObj, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                        <i className={getFileIcon(fileObj.name)} style={{ fontSize: '1.4rem', color: '#0f3d3e' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {fileObj.name}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>
                            {formatFileSize(fileObj.size)}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveFile(index)}
                        style={{ padding: '6px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>

        {/* ── Upload Progress ── */}
        {loading && uploadProgress > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '0.8rem', color: '#666' }}>
                {uploadProgress < 100 ? 'Uploading to server...' : 'Processing...'}
              </span>
              <span style={{ fontSize: '0.8rem', color: '#0f3d3e', fontWeight: '600' }}>
                {uploadProgress}%
              </span>
            </div>
            <div style={{ background: '#e9ecef', borderRadius: '8px', height: '8px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${uploadProgress}%`, background: 'linear-gradient(90deg, #0f3d3e, #1a5555)', borderRadius: '8px', transition: 'width 0.3s ease' }} />
            </div>
          </div>
        )}

        {/* ── Action Buttons ── */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <GlobalButton
            label="Cancel"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          />
          <GlobalButton
            label={
              loading
                ? uploadProgress < 100
                  ? `Uploading ${uploadProgress}%...`
                  : editingMaterial ? 'Updating...' : 'Creating...'
                : editingMaterial ? 'Update Material' : 'Create Material'
            }
            variant="primary"
            onClick={handleSubmit}
            disabled={loading}
            loading={loading}
          />
        </div>
      </div>

      <MediaPickerDialog
        visible={pickerOpen}
        onHide={() => setPickerOpen(false)}
        maxSelectable={Math.max(0, MAX_FILES - uploadedFiles.length - existingFileCount)}
        initiallySelected={libraryFiles}
        onConfirm={(items) => setLibraryFiles(items)}
      />
    </Dialog>
  );
};

export default AddStudyMaterialModal;

// import React, { useState, useEffect, useRef, useCallback } from 'react';
// import { Dialog } from 'primereact/dialog';
// import { Message } from 'primereact/message';
// import { Toast } from 'primereact/toast';
// import { io, Socket } from 'socket.io-client';
// import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
// import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
// import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
// import { GlobalTextArea } from '../../../components/GlobalComponents/GlobalTextArea';
// import { GlobalDropdownField, DropdownOption } from '../../../components/GlobalComponents/GlobalDropdownField';

// interface StudyMaterialFile {
//   file_name: string;
//   key: string;
//   url: string;
//   size: number;
//   mime_type: string;
//   uploaded_at: string;
// }

// interface StudyMaterial {
//   id: string;
//   chapter_id: string;
//   module_id: string;
//   course_id: string;
//   institute_id: string;
//   title: string;
//   body: string;
//   status: string;
//   files: StudyMaterialFile[];
//   video_links: string[];
//   created_at: string;
//   created_by: string;
// }

// interface AddStudyMaterialModalProps {
//   visible: boolean;
//   chapterId: string;
//   editingMaterial?: StudyMaterial | null;
//   onHide: () => void;
//   onSuccess?: () => void;
// }

// interface UploadedFile {
//   file: File;
//   name: string;
//   size: number;
// }

// interface UploadedVideo {
//   file: File;
//   name: string;
//   size: number;
// }

// interface VideoStatusEvent {
//   video_id:      string;
//   status:        'ready' | 'failed';
//   m3u8_url:      string | null;
//   error_message: string | null;
//   updated_at:    string;
// }

// // ── Socket singleton — one connection per browser session ──────────────────
// let globalSocket: Socket | null = null;

// const getOrCreateSocket = async (): Promise<Socket | null> => {
//   // Reuse existing connected socket
//   if (globalSocket?.connected) return globalSocket;

//   try {
//     // 1. Get short-lived socket token from PHP
//     const res = await apiClient.get('/v1/socket-token');
//     const { socket_token } = res.data;

//     if (!socket_token) throw new Error('No socket_token in response');

//     // 2. Connect to Node WebSocket
//     globalSocket = io(window.location.origin, {
//       path:        '/node-ws/socket.io',
//       auth:        { socket_token },
//       reconnection: true,
//       reconnectionAttempts: 5,
//       reconnectionDelay:    2000,
//     });

//     return globalSocket;

//   } catch (err) {
//     console.error('Socket init failed:', err);
//     return null;
//   }
// };

// const disconnectSocket = () => {
//   if (globalSocket) {
//     globalSocket.disconnect();
//     globalSocket = null;
//   }
// };

// // ── Component ──────────────────────────────────────────────────────────────

// const AddStudyMaterialModal: React.FC<AddStudyMaterialModalProps> = ({
//   visible,
//   chapterId,
//   editingMaterial,
//   onHide,
//   onSuccess,
// }) => {
//   const toast         = useRef<Toast>(null);
//   const fileInputRef  = useRef<HTMLInputElement>(null);
//   const videoInputRef = useRef<HTMLInputElement>(null);

//   // Track video_ids we are waiting on so we can match incoming socket events
//   const pendingVideoIds = useRef<Set<string>>(new Set());
//   const socketRef       = useRef<Socket | null>(null);

//   const [loading, setLoading]               = useState(false);
//   const [uploadProgress, setUploadProgress] = useState(0);
//   const [error, setError]                   = useState<string | null>(null);
//   const [uploadedFiles, setUploadedFiles]   = useState<UploadedFile[]>([]);
//   const [uploadedVideos, setUploadedVideos] = useState<UploadedVideo[]>([]);

//   // Track per-video processing status to show inline badges
//   const [videoProcessingStatus, setVideoProcessingStatus] = useState<
//     Record<string, 'processing' | 'ready' | 'failed'>
//   >({});

//   const [formData, setFormData] = useState({
//     title:       '',
//     body:        '',
//     status:      'active',
//     video_links: [''],
//   });

//   const [errors, setErrors] = useState<Record<string, string>>({});

//   const MAX_FILES           = 3;
//   const MAX_TOTAL_SIZE      = 10 * 1024 * 1024;
//   const ALLOWED_FORMATS     = [
//     'application/pdf',
//     'application/vnd.ms-excel',
//     'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
//     'application/msword',
//     'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
//     'image/jpeg', 'image/jpg', 'image/png',
//   ];
//   const ALLOWED_EXTENSIONS  = ['pdf', 'xls', 'xlsx', 'doc', 'docx', 'jpeg', 'jpg', 'png'];
//   const MAX_VIDEOS           = 3;
//   const MAX_VIDEO_SIZE       = 500 * 1024 * 1024;
//   const ALLOWED_VIDEO_TYPES  = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska'];
//   const ALLOWED_VIDEO_EXTS   = ['mp4', 'mov', 'avi', 'mkv'];

//   const statusOptions: DropdownOption[] = [
//     { label: 'Active',   value: 'active'   },
//     { label: 'Inactive', value: 'inactive' },
//     { label: 'Draft',    value: 'draft'    },
//   ];

//   const showSuccess = (summary: string, detail: string) => {
//     toast.current?.show({ severity: 'success', summary, detail, life: 3200 });
//   };

//   const showError = (detail: string, summary = 'Error') => {
//     toast.current?.show({ severity: 'error', summary, detail, life: 4500 });
//   };

//   // ── Handle incoming video_status events ───────────────────────────────────
//   const handleVideoStatus = useCallback((data: VideoStatusEvent) => {
//     // Only care about videos we uploaded in this session
//     if (!pendingVideoIds.current.has(data.video_id)) return;

//     setVideoProcessingStatus((prev) => ({ ...prev, [data.video_id]: data.status }));

//     if (data.status === 'ready') {
//       toast.current?.show({
//         severity: 'success',
//         summary:  '✅ Video Ready',
//         detail:   `A video has finished processing and is now available.`,
//         life:     5000,
//       });
//       pendingVideoIds.current.delete(data.video_id);
//       onSuccess?.(); // Refresh the study material list to show updated video
//     }

//     if (data.status === 'failed') {
//       toast.current?.show({
//         severity: 'error',
//         summary:  '❌ Video Processing Failed',
//         detail:   data.error_message || 'A video failed to process. Please try uploading again.',
//         life:     6000,
//       });
//       pendingVideoIds.current.delete(data.video_id);
//     }

//     // Clean up socket if no more pending videos
//     if (pendingVideoIds.current.size === 0) {
//       socketRef.current?.off('video_status', handleVideoStatus);
//     }
//   }, [onSuccess]);

//   // ── Connect socket and register listener after upload ─────────────────────
//   const initSocketListener = useCallback(async () => {
//     const socket = await getOrCreateSocket();
//     if (!socket) {
//       console.warn('Could not connect to WebSocket — video status updates unavailable');
//       return;
//     }

//     socketRef.current = socket;

//     // Remove any stale listener before adding fresh one
//     socket.off('video_status', handleVideoStatus);
//     socket.on('video_status', handleVideoStatus);

//     socket.on('connect', () => {
//       console.log('🔌 Socket connected:', socket.id);
//     });

//     socket.on('connect_error', (err) => {
//       console.error('Socket connect_error:', err.message);
//     });
//   }, [handleVideoStatus]);

//   // ── Cleanup on unmount ────────────────────────────────────────────────────
//   useEffect(() => {
//     return () => {
//       // Don't disconnect on unmount — socket is global and may still be
//       // receiving events for pending videos. Just remove this component's listener.
//       socketRef.current?.off('video_status', handleVideoStatus);
//     };
//   }, [handleVideoStatus]);

//   useEffect(() => {
//     if (editingMaterial) {
//       setFormData({
//         title:       editingMaterial.title,
//         body:        editingMaterial.body,
//         status:      editingMaterial.status || 'active',
//         video_links: editingMaterial.video_links.length > 0 ? editingMaterial.video_links : [''],
//       });
//       setUploadedFiles([]);
//       setUploadedVideos([]);
//     } else {
//       setFormData({ title: '', body: '', status: 'active', video_links: [''] });
//       setUploadedFiles([]);
//       setUploadedVideos([]);
//     }
//     setErrors({});
//     setError(null);
//     setUploadProgress(0);
//     setVideoProcessingStatus({});
//     pendingVideoIds.current.clear();
//   }, [editingMaterial, visible]);

//   const handleInputChange = (field: string, value: any) => {
//     setFormData((prev) => ({ ...prev, [field]: value }));
//     if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
//   };

//   const handleVideoLinkChange = (index: number, value: string) => {
//     const newLinks = [...formData.video_links];
//     newLinks[index] = value;
//     setFormData((prev) => ({ ...prev, video_links: newLinks }));
//   };

//   const addVideoLink = () => {
//     if (formData.video_links.length < 5) {
//       setFormData((prev) => ({ ...prev, video_links: [...prev.video_links, ''] }));
//     }
//   };

//   const removeVideoLink = (index: number) => {
//     const newLinks = formData.video_links.filter((_, i) => i !== index);
//     setFormData((prev) => ({ ...prev, video_links: newLinks.length > 0 ? newLinks : [''] }));
//   };

//   const formatFileSize = (bytes: number): string => {
//     if (bytes === 0) return '0 Bytes';
//     const k     = 1024;
//     const sizes = ['Bytes', 'KB', 'MB', 'GB'];
//     const i     = Math.floor(Math.log(bytes) / Math.log(k));
//     return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
//   };

//   const getFileExtension = (filename: string): string =>
//     filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();

//   const getTotalFilesSize = (): number =>
//     uploadedFiles.reduce((total, f) => total + f.size, 0);

//   const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
//     const files = event.target.files;
//     if (!files || files.length === 0) return;

//     if (uploadedFiles.length + files.length > MAX_FILES) {
//       showError(`Maximum ${MAX_FILES} files allowed`);
//       if (fileInputRef.current) fileInputRef.current.value = '';
//       return;
//     }

//     const newFiles: UploadedFile[] = [];
//     const fileErrors: string[]     = [];

//     Array.from(files).forEach((file) => {
//       const ext = getFileExtension(file.name);
//       if (!ALLOWED_FORMATS.includes(file.type) && !ALLOWED_EXTENSIONS.includes(ext)) {
//         fileErrors.push(`${file.name}: Invalid format`);
//         return;
//       }
//       newFiles.push({ file, name: file.name, size: file.size });
//     });

//     const wouldBeTotal = getTotalFilesSize() + newFiles.reduce((s, f) => s + f.size, 0);
//     if (wouldBeTotal > MAX_TOTAL_SIZE) {
//       showError(`Total size cannot exceed ${formatFileSize(MAX_TOTAL_SIZE)}`);
//       if (fileInputRef.current) fileInputRef.current.value = '';
//       return;
//     }

//     if (fileErrors.length > 0) showError(fileErrors.join(', '));
//     setUploadedFiles((prev) => [...prev, ...newFiles]);
//     if (fileInputRef.current) fileInputRef.current.value = '';
//   };

//   const handleRemoveFile  = (index: number) =>
//     setUploadedFiles((prev) => prev.filter((_, i) => i !== index));

//   const handleVideoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
//     const files = event.target.files;
//     if (!files || files.length === 0) return;

//     if (uploadedVideos.length + files.length > MAX_VIDEOS) {
//       showError(`Maximum ${MAX_VIDEOS} videos allowed`);
//       if (videoInputRef.current) videoInputRef.current.value = '';
//       return;
//     }

//     const newVideos: UploadedVideo[] = [];
//     const videoErrors: string[]      = [];

//     Array.from(files).forEach((file) => {
//       const ext = getFileExtension(file.name);
//       if (!ALLOWED_VIDEO_TYPES.includes(file.type) && !ALLOWED_VIDEO_EXTS.includes(ext)) {
//         videoErrors.push(`${file.name}: Invalid format. Allowed: MP4, MOV, AVI, MKV`);
//         return;
//       }
//       if (file.size > MAX_VIDEO_SIZE) {
//         videoErrors.push(`${file.name}: Exceeds 500MB limit`);
//         return;
//       }
//       newVideos.push({ file, name: file.name, size: file.size });
//     });

//     if (videoErrors.length > 0) {
//       showError(videoErrors.join('\n'));
//       if (videoInputRef.current) videoInputRef.current.value = '';
//       return;
//     }

//     setUploadedVideos((prev) => [...prev, ...newVideos]);
//     if (videoInputRef.current) videoInputRef.current.value = '';
//   };

//   const handleRemoveVideo = (index: number) =>
//     setUploadedVideos((prev) => prev.filter((_, i) => i !== index));

//   const getFileIcon = (filename: string): string => {
//     const ext = getFileExtension(filename);
//     switch (ext) {
//       case 'pdf':               return 'pi pi-file-pdf';
//       case 'doc': case 'docx':  return 'pi pi-file-word';
//       case 'xls': case 'xlsx':  return 'pi pi-file-excel';
//       case 'jpg': case 'jpeg':
//       case 'png':               return 'pi pi-image';
//       default:                  return 'pi pi-file';
//     }
//   };

//   const validateForm = () => {
//     const newErrors: Record<string, string> = {};

//     if (!formData.title.trim()) newErrors.title  = 'Title is required';
//     if (!formData.body.trim())  newErrors.body   = 'Content is required';
//     if (!formData.status)       newErrors.status = 'Status is required';

//     const validLinks = formData.video_links.filter((l) => l.trim() !== '');
//     if (validLinks.length > 5) {
//       newErrors.video_links = 'Maximum 5 video links allowed';
//     }
//     for (const link of validLinks) {
//       try { new URL(link); } catch {
//         newErrors.video_links = 'All video links must be valid URLs';
//         break;
//       }
//     }

//     setErrors(newErrors);
//     if (Object.keys(newErrors).length > 0) {
//       showError('Please correct all highlighted fields', 'Validation Failed');
//       return false;
//     }
//     return true;
//   };

//   const handleSubmit = async () => {
//     if (!validateForm()) return;

//     setLoading(true);
//     setUploadProgress(0);

//     const payload = new FormData();
//     payload.append('chapter_id', chapterId);
//     payload.append('title',      formData.title.trim());
//     payload.append('body',       formData.body.trim());
//     payload.append('status',     formData.status);

//     formData.video_links
//       .filter((l) => l.trim() !== '')
//       .forEach((link) => payload.append('video_links[]', link.trim()));

//     uploadedFiles.forEach((f)  => payload.append('files[]',  f.file));
//     uploadedVideos.forEach((v) => payload.append('videos[]', v.file));

//     try {
//       const response = await apiClient.post('/v1/study-materials', payload, {
//         headers: { 'Content-Type': 'multipart/form-data' },
//         onUploadProgress: (progressEvent) => {
//           const percent = Math.round(
//             (progressEvent.loaded * 100) / (progressEvent.total ?? 1)
//           );
//           setUploadProgress(percent);
//         },
//       });

//       if (response.data.success) {
//         const videosData: any[]  = response.data.data?.videos ?? [];
//         const videosProcessing   = response.data.data?.videos_processing ?? 0;

//         // ── Register pending video IDs and init socket ───────────
//         if (videosProcessing > 0 && videosData.length > 0) {
//           videosData.forEach((v: any) => {
//             if (v.video_id) {
//               pendingVideoIds.current.add(v.video_id);
//               setVideoProcessingStatus((prev) => ({ ...prev, [v.video_id]: 'processing' }));
//             }
//           });

//           // Connect socket AFTER we know the video IDs
//           await initSocketListener();

//           showSuccess(
//             'Study Material Created',
//             `Created successfully. ${videosProcessing} video(s) are processing — you'll be notified here when ready.`
//           );
//         } else {
//           showSuccess(
//             'Study Material Created',
//             'The study material has been created successfully.'
//           );
//         }

//         onSuccess?.();
//         setTimeout(() => onHide(), 900);
//       } else {
//         showError(response.data.message || 'Operation failed');
//       }
//     } catch (err: any) {
//       console.error('Full error:', err.response);
//       const msg =
//         err.response?.data?.error   ||
//         err.response?.data?.message ||
//         `HTTP ${err.response?.status}: Failed to save study material.`;
//       showError(msg);
//     } finally {
//       setLoading(false);
//       setUploadProgress(0);
//     }
//   };

//   const handleClose = () => {
//     setFormData({ title: '', body: '', status: 'active', video_links: [''] });
//     setUploadedFiles([]);
//     setUploadedVideos([]);
//     setErrors({});
//     setError(null);
//     setUploadProgress(0);
//     setVideoProcessingStatus({});
//     if (fileInputRef.current)  fileInputRef.current.value  = '';
//     if (videoInputRef.current) videoInputRef.current.value = '';
//     onHide();
//   };

//   const totalSize      = getTotalFilesSize();
//   const canUploadMore  = uploadedFiles.length  < MAX_FILES  && totalSize < MAX_TOTAL_SIZE;
//   const canUploadVideo = uploadedVideos.length < MAX_VIDEOS;

//   // How many videos are still processing (for showing spinner in button)
//   const processingCount = Object.values(videoProcessingStatus).filter(
//     (s) => s === 'processing'
//   ).length;

//   return (
//     <Dialog
//       visible={visible}
//       onHide={handleClose}
//       style={{ width: '90vw', maxWidth: '900px' }}
//       dismissableMask
//       draggable={false}
//       pt={{
//         root: {
//           style: {
//             borderRadius: '20px',
//             overflow: 'hidden',
//             boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
//           },
//         },
//       }}
//     >
//       <Toast ref={toast} position="top-right" />

//       <div>
//         {/* Header */}
//         <div
//           style={{
//             background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
//             borderRadius: '16px', padding: '24px', marginBottom: '24px',
//             color: 'white', position: 'relative', overflow: 'hidden',
//           }}
//         >
//           <div
//             style={{
//               position: 'absolute', top: '-50px', right: '-50px',
//               width: '150px', height: '150px',
//               background: 'rgba(212, 241, 126, 0.1)',
//               borderRadius: '50%', filter: 'blur(40px)',
//             }}
//           />
//           <div style={{ position: 'relative', zIndex: 1 }}>
//             <div
//               style={{
//                 display: 'inline-block', padding: '4px 10px',
//                 background: 'rgba(212, 241, 126, 0.2)', borderRadius: '16px',
//                 marginBottom: '8px', fontSize: '0.65rem', fontWeight: '600',
//                 letterSpacing: '0.5px', textTransform: 'uppercase',
//               }}
//             >
//               Study Material Management
//             </div>
//             <h2 style={{ margin: '0 0 6px 0', fontSize: '1.4rem', fontWeight: '700' }}>
//               {editingMaterial ? 'Edit Study Material' : 'Create Study Material'}
//             </h2>
//             <p style={{ margin: '0', color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
//               {editingMaterial
//                 ? 'Update study material details'
//                 : 'Share educational resources with students'}
//             </p>

//             {/* Live processing indicator shown in header when videos are processing */}
//             {processingCount > 0 && (
//               <div
//                 style={{
//                   marginTop: '12px', padding: '8px 14px',
//                   background: 'rgba(212, 241, 126, 0.15)',
//                   border: '1px solid rgba(212, 241, 126, 0.3)',
//                   borderRadius: '10px', display: 'flex',
//                   alignItems: 'center', gap: '8px',
//                   fontSize: '0.8rem',
//                 }}
//               >
//                 <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.9rem' }} />
//                 {processingCount} video{processingCount > 1 ? 's' : ''} processing in background...
//                 You'll be notified when ready.
//               </div>
//             )}
//           </div>
//         </div>

//         {error && (
//           <Message severity="error" text={error} style={{ marginBottom: '20px', fontSize: '0.9rem' }} />
//         )}

//         <div
//           style={{
//             background: 'white', borderRadius: '12px',
//             padding: '20px', marginBottom: '20px', border: '1px solid #f0f0f0',
//           }}
//         >
//           <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

//             {/* Title */}
//             <GlobalInputField
//               label="Material Title"
//               required
//               value={formData.title}
//               onChange={(e) => handleInputChange('title', e.target.value)}
//               placeholder="e.g., Introduction to Variables"
//               error={errors.title}
//             />

//             {/* Body */}
//             <GlobalTextArea
//               label="Material Content"
//               required
//               value={formData.body}
//               onChange={(e) => handleInputChange('body', e.target.value)}
//               placeholder="Write detailed content, notes, or instructions here..."
//               error={errors.body}
//               rows={8}
//             />

//             {/* Status */}
//             <GlobalDropdownField
//               label="Status"
//               required
//               value={formData.status}
//               onChange={(val) => handleInputChange('status', val)}
//               options={statusOptions}
//               placeholder="Select status"
//               error={errors.status}
//             />

//             {/* External Video Links */}
//             <div
//               style={{
//                 background: '#f8f9fa', padding: '16px',
//                 borderRadius: '12px', border: '1px solid #e9ecef',
//               }}
//             >
//               <div
//                 style={{
//                   display: 'flex', justifyContent: 'space-between',
//                   alignItems: 'center', marginBottom: '12px',
//                 }}
//               >
//                 <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#0f3d3e' }}>
//                   <i className="pi pi-link" style={{ marginRight: '8px' }} />
//                   External Video Links (Optional)
//                 </h3>
//                 <span style={{ fontSize: '0.8rem', color: '#666' }}>
//                   {formData.video_links.filter((l) => l.trim()).length}/5
//                 </span>
//               </div>

//               <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
//                 {formData.video_links.map((link, index) => (
//                   <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
//                     <GlobalInputField
//                       value={link}
//                       onChange={(e) => handleVideoLinkChange(index, e.target.value)}
//                       placeholder="https://youtube.com/watch?v=..."
//                       error=""
//                     />
//                     {formData.video_links.length > 1 && (
//                       <button
//                         type="button"
//                         onClick={() => removeVideoLink(index)}
//                         style={{
//                           padding: '10px 12px', background: '#fee2e2', color: '#dc2626',
//                           border: 'none', borderRadius: '8px', cursor: 'pointer', flexShrink: 0,
//                         }}
//                       >
//                         <i className="pi pi-trash" style={{ fontSize: '0.85rem' }} />
//                       </button>
//                     )}
//                   </div>
//                 ))}
//               </div>

//               {errors.video_links && (
//                 <small style={{ color: '#e53e3e', marginTop: '8px', display: 'block' }}>
//                   {errors.video_links}
//                 </small>
//               )}

//               {formData.video_links.length < 5 && (
//                 <button
//                   type="button"
//                   onClick={addVideoLink}
//                   style={{
//                     marginTop: '12px', padding: '8px 14px',
//                     background: 'white', border: '1px solid #e0e0e0',
//                     borderRadius: '8px', color: '#0f3d3e', cursor: 'pointer',
//                     fontSize: '0.85rem', fontWeight: '500', width: '100%',
//                   }}
//                 >
//                   <i className="pi pi-plus" style={{ marginRight: '6px' }} />
//                   Add Another Video Link
//                 </button>
//               )}
//             </div>

//             {/* Video File Uploads */}
//             <div
//               style={{
//                 background: '#f8f9fa', padding: '16px',
//                 borderRadius: '12px', border: '1px solid #e9ecef',
//               }}
//             >
//               <div
//                 style={{
//                   display: 'flex', justifyContent: 'space-between',
//                   alignItems: 'center', marginBottom: '12px',
//                 }}
//               >
//                 <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#0f3d3e' }}>
//                   <i className="pi pi-video" style={{ marginRight: '8px' }} />
//                   Upload Videos (Optional)
//                 </h3>
//                 <span style={{ fontSize: '0.8rem', color: '#666' }}>
//                   {uploadedVideos.length}/{MAX_VIDEOS} • Max 500MB each
//                 </span>
//               </div>

//               <input
//                 ref={videoInputRef}
//                 type="file"
//                 multiple
//                 accept=".mp4,.mov,.avi,.mkv"
//                 onChange={handleVideoSelect}
//                 style={{ display: 'none' }}
//               />

//               <button
//                 type="button"
//                 onClick={() => videoInputRef.current?.click()}
//                 disabled={!canUploadVideo}
//                 style={{
//                   width: '100%', padding: '14px',
//                   border: '2px dashed #cbd5e0', borderRadius: '8px',
//                   background: canUploadVideo ? 'white' : '#f5f5f5',
//                   color: canUploadVideo ? '#0f3d3e' : '#999',
//                   cursor: canUploadVideo ? 'pointer' : 'not-allowed',
//                   fontSize: '0.9rem', fontWeight: '500',
//                 }}
//               >
//                 <i className="pi pi-upload" style={{ marginRight: '8px' }} />
//                 {canUploadVideo ? 'Choose video files' : 'Limit reached (3 videos max)'}
//               </button>

//               <div style={{ marginTop: '8px', fontSize: '0.75rem', color: '#666', textAlign: 'center' }}>
//                 Allowed: MP4, MOV, AVI, MKV • Videos are processed in background after upload
//               </div>

//               {uploadedVideos.length > 0 && (
//                 <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
//                   {uploadedVideos.map((videoObj, index) => {
//                     // We don't have video_id yet before upload — show generic pending state
//                     return (
//                       <div
//                         key={index}
//                         style={{
//                           display: 'flex', alignItems: 'center', justifyContent: 'space-between',
//                           padding: '10px 12px', background: 'white',
//                           border: '1px solid #e0e0e0', borderRadius: '8px',
//                         }}
//                       >
//                         <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
//                           <i className="pi pi-play-circle" style={{ fontSize: '1.4rem', color: '#0f3d3e' }} />
//                           <div style={{ flex: 1, minWidth: 0 }}>
//                             <div
//                               style={{
//                                 fontSize: '0.85rem', fontWeight: '500',
//                                 whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
//                               }}
//                             >
//                               {videoObj.name}
//                             </div>
//                             <div style={{ fontSize: '0.75rem', color: '#666', display: 'flex', gap: '8px', alignItems: 'center' }}>
//                               <span>{formatFileSize(videoObj.size)}</span>
//                               <span
//                                 style={{
//                                   padding: '1px 8px', background: '#fef3c7',
//                                   color: '#92400e', borderRadius: '10px', fontSize: '0.7rem',
//                                 }}
//                               >
//                                 Will be processed after upload
//                               </span>
//                             </div>
//                           </div>
//                         </div>
//                         <button
//                           onClick={() => handleRemoveVideo(index)}
//                           style={{
//                             padding: '6px 10px', background: '#fee2e2', color: '#dc2626',
//                             border: 'none', borderRadius: '6px', cursor: 'pointer',
//                           }}
//                         >
//                           Remove
//                         </button>
//                       </div>
//                     );
//                   })}
//                 </div>
//               )}

//               {/* Post-upload: show live processing status per video_id */}
//               {Object.entries(videoProcessingStatus).length > 0 && (
//                 <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
//                   {Object.entries(videoProcessingStatus).map(([videoId, status]) => (
//                     <div
//                       key={videoId}
//                       style={{
//                         display: 'flex', alignItems: 'center', gap: '8px',
//                         padding: '8px 12px', borderRadius: '8px',
//                         background: status === 'ready'
//                           ? '#f0fdf4'
//                           : status === 'failed'
//                           ? '#fef2f2'
//                           : '#fffbeb',
//                         border: `1px solid ${
//                           status === 'ready'
//                             ? '#bbf7d0'
//                             : status === 'failed'
//                             ? '#fecaca'
//                             : '#fde68a'
//                         }`,
//                         fontSize: '0.8rem',
//                       }}
//                     >
//                       {status === 'processing' && (
//                         <i className="pi pi-spin pi-spinner" style={{ color: '#d97706' }} />
//                       )}
//                       {status === 'ready' && (
//                         <i className="pi pi-check-circle" style={{ color: '#16a34a' }} />
//                       )}
//                       {status === 'failed' && (
//                         <i className="pi pi-times-circle" style={{ color: '#dc2626' }} />
//                       )}
//                       <span style={{
//                         color: status === 'ready'
//                           ? '#15803d'
//                           : status === 'failed'
//                           ? '#dc2626'
//                           : '#92400e',
//                       }}>
//                         {videoId} —{' '}
//                         {status === 'processing' && 'Processing...'}
//                         {status === 'ready'      && 'Ready ✓'}
//                         {status === 'failed'     && 'Processing failed'}
//                       </span>
//                     </div>
//                   ))}
//                 </div>
//               )}
//             </div>

//             {/* File Attachments */}
//             <div
//               style={{
//                 background: '#f8f9fa', padding: '16px',
//                 borderRadius: '12px', border: '1px solid #e9ecef',
//               }}
//             >
//               <div
//                 style={{
//                   display: 'flex', justifyContent: 'space-between',
//                   alignItems: 'center', marginBottom: '12px',
//                 }}
//               >
//                 <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#0f3d3e' }}>
//                   <i className="pi pi-paperclip" style={{ marginRight: '8px' }} />
//                   File Attachments (Optional)
//                 </h3>
//                 <span style={{ fontSize: '0.8rem', color: '#666' }}>
//                   {uploadedFiles.length}/{MAX_FILES} • {formatFileSize(totalSize)} / {formatFileSize(MAX_TOTAL_SIZE)}
//                 </span>
//               </div>

//               <input
//                 ref={fileInputRef}
//                 type="file"
//                 multiple
//                 accept=".pdf,.xls,.xlsx,.doc,.docx,.jpg,.jpeg,.png"
//                 onChange={handleFileSelect}
//                 style={{ display: 'none' }}
//               />

//               <button
//                 type="button"
//                 onClick={() => fileInputRef.current?.click()}
//                 disabled={!canUploadMore}
//                 style={{
//                   width: '100%', padding: '14px',
//                   border: '2px dashed #cbd5e0', borderRadius: '8px',
//                   background: canUploadMore ? 'white' : '#f5f5f5',
//                   color: canUploadMore ? '#0f3d3e' : '#999',
//                   cursor: canUploadMore ? 'pointer' : 'not-allowed',
//                   fontSize: '0.9rem', fontWeight: '500',
//                 }}
//               >
//                 <i className="pi pi-upload" style={{ marginRight: '8px' }} />
//                 {canUploadMore ? 'Choose files (max 10MB total)' : 'Limit reached'}
//               </button>

//               <div style={{ marginTop: '8px', fontSize: '0.75rem', color: '#666', textAlign: 'center' }}>
//                 Allowed: PDF, Word, Excel, JPEG, PNG
//               </div>

//               {uploadedFiles.length > 0 && (
//                 <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
//                   {uploadedFiles.map((fileObj, index) => (
//                     <div
//                       key={index}
//                       style={{
//                         display: 'flex', alignItems: 'center', justifyContent: 'space-between',
//                         padding: '10px 12px', background: 'white',
//                         border: '1px solid #e0e0e0', borderRadius: '8px',
//                       }}
//                     >
//                       <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
//                         <i className={getFileIcon(fileObj.name)} style={{ fontSize: '1.4rem', color: '#0f3d3e' }} />
//                         <div style={{ flex: 1, minWidth: 0 }}>
//                           <div
//                             style={{
//                               fontSize: '0.85rem', fontWeight: '500',
//                               whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
//                             }}
//                           >
//                             {fileObj.name}
//                           </div>
//                           <div style={{ fontSize: '0.75rem', color: '#666' }}>
//                             {formatFileSize(fileObj.size)}
//                           </div>
//                         </div>
//                       </div>
//                       <button
//                         onClick={() => handleRemoveFile(index)}
//                         style={{
//                           padding: '6px 10px', background: '#fee2e2', color: '#dc2626',
//                           border: 'none', borderRadius: '6px', cursor: 'pointer',
//                         }}
//                       >
//                         Remove
//                       </button>
//                     </div>
//                   ))}
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>

//         {/* Upload Progress Bar */}
//         {loading && uploadProgress > 0 && (
//           <div style={{ marginBottom: '16px' }}>
//             <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
//               <span style={{ fontSize: '0.8rem', color: '#666' }}>
//                 {uploadProgress < 100 ? 'Uploading to server...' : 'Processing...'}
//               </span>
//               <span style={{ fontSize: '0.8rem', color: '#0f3d3e', fontWeight: '600' }}>
//                 {uploadProgress}%
//               </span>
//             </div>
//             <div
//               style={{
//                 background: '#e9ecef', borderRadius: '8px',
//                 height: '8px', overflow: 'hidden',
//               }}
//             >
//               <div
//                 style={{
//                   height: '100%',
//                   width: `${uploadProgress}%`,
//                   background: 'linear-gradient(90deg, #0f3d3e, #1a5555)',
//                   borderRadius: '8px',
//                   transition: 'width 0.3s ease',
//                 }}
//               />
//             </div>
//           </div>
//         )}

//         {/* Action Buttons */}
//         <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
//           <GlobalButton
//             label="Cancel"
//             variant="outline"
//             onClick={handleClose}
//             disabled={loading}
//           />
//           <GlobalButton
//             label={
//               loading
//                 ? uploadProgress < 100
//                   ? `Uploading ${uploadProgress}%...`
//                   : editingMaterial ? 'Updating...' : 'Creating...'
//                 : editingMaterial ? 'Update Material' : 'Create Material'
//             }
//             variant="primary"
//             onClick={handleSubmit}
//             disabled={loading}
//             loading={loading}
//           />
//         </div>
//       </div>
//     </Dialog>
//   );
// };

// export default AddStudyMaterialModal;

